import log from 'loglevel';
import { useEffect, useState } from 'react';
import { Card, Paper } from '@mui/material';
import { AxiosError } from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTablePageParams } from '../../hooks/useTablePageParams';
import {
  useGetAccountsInfo,
  useDeleteAccount,
} from '../../hooks/query/useAccounts';
import STRING_KEYS from '../../language/keys';
import useNavigate from '../../hooks/useNavigate';
import { Business } from '../../icons/business';
import ListPageContainer from '../../components/ListPageContainer';
import { useGetAccount } from '../../hooks/useAccount';
import AccountEditPanel from '../AccountEditPanel';
import { AccountStatusEnum } from '../../consts/enums';
import { ACCOUNTS_PAGE_FILTERS } from './accountsPageFilters';
import AccountsFilters from './AccountsFilters';
import { AccountsTable } from './AccountsTable';
import style from './style';

const TAG = '[ACCOUNTS_PAGE]';

export default function AccountsPage(): JSX.Element {
  const { goToAccounts, goToAccountEdit } = useNavigate();
  const { accountId: routeAccountId = '' } = useParams();
  const [deleteError, setDeleteError] = useState('');
  const { t: translate } = useTranslation();
  const handleClose = (preservedAccountsFiltersBase64: string | null) => {
    goToAccounts(preservedAccountsFiltersBase64);
  };
  const [searchParams] = useSearchParams();

  const {
    activeFilters,
    onChangeFilter,
    currentPage,
    activeSort,
    onSortChange,
    onPageChange,
  } = useTablePageParams([
    ACCOUNTS_PAGE_FILTERS.QUERY,
    ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS,
    ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN,
    ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN,
  ]);

  useEffect(() => {
    if (!searchParams.get(ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS)) {
      onChangeFilter(ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS,
        [AccountStatusEnum.active]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    error, data, isLoading, refetch: refetchAccountsList,
  } = useGetAccountsInfo(
    activeFilters,
    currentPage,
    activeSort,
  );

  const { mutateAsync: deleteAccount } = useDeleteAccount(() => goToAccounts());
  const {
    account,
    isLoading: isLoadingAccount,
    isError: isAccountError,
    error: errorAccount,
  } = useGetAccount(routeAccountId);

  const handleChangePage = (newPageNumber: number): void => {
    onPageChange(newPageNumber);
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      await deleteAccount({
        accountId,
      });

      log.debug(TAG, `account ${accountId} has been deleted.`);
      await refetchAccountsList();
    } catch (e) {
      const err = e as AxiosError;
      const internalErrorCode = err.response?.data.internalErrorCode;
      const errorMessage = translate(STRING_KEYS.ACCOUNT_ERRORS.DELETE, {
        internalErrorCode,
      });
      setDeleteError(errorMessage);
    }
  };

  const handleEditAccount = (id: string) => {
    goToAccountEdit(id, searchParams.toString(), btoa(searchParams.toString()));
  };

  return (
    <ListPageContainer
      title={ {
        text: translate(STRING_KEYS.LAYOUT_ACCOUNTS_TITLE),
        icon: <Business />,
      } }
    >
      <Card sx={ style.tableWrapper.sx }>
        <AccountsFilters
          activeFilters={ activeFilters }
          onChangeFilter={ onChangeFilter }
        />
        <Paper>
          <AccountsTable
            page={ currentPage - 1 }
            accountsData={ data }
            error={ error || deleteError }
            isLoading={ isLoading }
            onPageChange={ handleChangePage }
            activeSort={ activeSort }
            onSortClick={ onSortChange }
            onDeleteHandler={ handleDeleteAccount }
            onEditAccount={ handleEditAccount }
          />
        </Paper>

        {routeAccountId && (
          <AccountEditPanel
            onCloseClick={ handleClose }
            isOpen={ !!routeAccountId }
            account={ account || null }
            onDeleteAccount={ handleDeleteAccount }
            onUpdateCompanyCompleted={ refetchAccountsList }
            isLoadingAccount={ isLoadingAccount }
            isErrorAccount={ isAccountError }
            accountErrorMessage={ errorAccount?.response?.data?.message ||
              translate(STRING_KEYS.ERROR_MESSAGE_GET_ACCOUNT) }
          />
        )}
      </Card>
    </ListPageContainer>
  );
}
